import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { PenSquare, ChevronRight, RefreshCw, Inbox, Sparkles, Settings2, Check,
         Search, X, Loader2, SlidersHorizontal, Paperclip, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LS_KEY = 'mariani_visible_categories';

function formatToday() {
  return new Date().toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function CategoryCard({ cat, counts, lastEmail, onClick }) {
  const total = counts?.total || 0;
  const unread = counts?.unread || 0;
  return (
    <div
      data-testid={`category-card-${cat.id}`}
      onClick={onClick}
      className="rounded-2xl p-5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      style={{ background: cat.bg_color }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-base" style={{ color: cat.color }}>{cat.name}</span>
        <span className="w-3 h-3 rounded-full" style={{ background: cat.color, opacity: 0.6 }} />
      </div>
      {/* Non lette in evidenza */}
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-4xl font-bold" style={{ color: cat.color, fontFamily: 'Inter, sans-serif' }}>
          {unread}
        </span>
        <span className="text-sm font-medium" style={{ color: cat.color, opacity: 0.85 }}>
          {unread === 1 ? 'non letta' : 'non lette'}
        </span>
      </div>
      {/* Totale sotto, più piccolo */}
      <div className="text-xs mb-4" style={{ color: cat.color, opacity: 0.55 }}>
        Totale: {total} {total === 1 ? 'email' : 'email'}
      </div>
      {lastEmail ? (
        <div className="text-xs mb-4 leading-relaxed" style={{ color: cat.color, opacity: 0.7 }}>
          {lastEmail.from_name || lastEmail.from_addr} — {lastEmail.subject?.slice(0, 40)}{lastEmail.subject?.length > 40 ? '...' : ''}
        </div>
      ) : (
        <div className="text-xs mb-4" style={{ color: cat.color, opacity: 0.5 }}>Nessuna email</div>
      )}
      <div className="flex items-center justify-between">
        {unread > 0 ? (
          <span className="text-xs px-2.5 py-1 rounded-full font-medium text-white" style={{ background: cat.color }}>
            {unread === 1 ? '• 1 nuova' : `• ${unread} nuove`}
          </span>
        ) : (
          <span className="text-xs" style={{ color: cat.color, opacity: 0.5 }}>nessuna nuova</span>
        )}
        <ChevronRight size={14} style={{ color: cat.color, opacity: 0.6 }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { accounts, unreadCounts, fetchEmails, refreshUnreadCounts, openCompose,
          categoryCount, loadCategoryCount, customCategories } = useApp();
  const [lastEmails, setLastEmails] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [visibleCats, setVisibleCats] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || null; } catch { return null; }
  });

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', has_attachments: false, folder: '', account_id: '' });
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const hasActiveFilters = filters.date_from || filters.date_to || filters.has_attachments || filters.folder || filters.account_id;

  const doSearch = useCallback(async (q, activeFilters) => {
    const filt = activeFilters || filters;
    const noQuery = !q || q.trim().length < 2;
    const noFilters = !filt.date_from && !filt.date_to && !filt.has_attachments && !filt.folder && !filt.account_id;
    if (noQuery && noFilters) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const params = { limit: 50 };
      if (q && q.trim().length >= 2) params.q = q.trim();
      if (filt.date_from) params.date_from = filt.date_from;
      if (filt.date_to) params.date_to = filt.date_to;
      if (filt.has_attachments) params.has_attachments = '1';
      if (filt.folder) params.folder = filt.folder;
      if (filt.account_id) params.account_id = filt.account_id;
      const res = await axios.get(`${API}/search`, { params });
      setSearchResults(res.data);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, [filters]); // eslint-disable-line

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim() && !hasActiveFilters) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(() => doSearch(val, filters), 350);
  };

  const applyFilters = () => {
    doSearch(searchQuery, filters);
    setSearchFocused(true);
  };

  const resetFilters = () => {
    const empty = { date_from: '', date_to: '', has_attachments: false, folder: '', account_id: '' };
    setFilters(empty);
    doSearch(searchQuery, empty);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    resetFilters();
    searchRef.current?.focus();
  };

  const handleResultClick = (email) => {
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/account/${email.account_id}/${email.folder}/${email.uid}`);
  };

  // Close results on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-search-container]')) {
        setSearchFocused(false);
        setSearchResults([]);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // which categories to show: null = all
  const visibleCategories = customCategories.filter(c =>
    !visibleCats || visibleCats.includes(c.id)
  );

  const totalUnread = Object.values(unreadCounts).reduce((s, a) => s + (a.inbox || 0), 0);

  useEffect(() => {
    loadCategoryCount();
    loadLastEmails();
  }, [accounts, customCategories]);

  const loadLastEmails = async () => {
    const map = {};
    for (const acc of accounts) {
      try {
        const emails = await fetchEmails(acc.id, 'inbox');
        emails.forEach(em => {
          const cat = em.category;
          if (cat && (!map[cat] || new Date(em.date_str) > new Date(map[cat].date_str))) map[cat] = em;
        });
      } catch {}
    }
    setLastEmails(map);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all(accounts.map(acc => fetchEmails(acc.id, 'inbox', true)));
      await loadLastEmails();
      await refreshUnreadCounts();
      await loadCategoryCount();
      toast.success('Email aggiornate');
    } catch { toast.error('Errore nell\'aggiornamento'); }
    finally { setRefreshing(false); }
  };

  const handleAiCategorize = async () => {
    setAiCategorizing(true);
    try {
      const res = await axios.post(`${API}/ai-categorize-all`);
      await loadCategoryCount();
      await loadLastEmails();
      toast.success(res.data.message || 'Categorizzazione AI completata');
    } catch { toast.error('Errore nella categorizzazione AI'); }
    finally { setAiCategorizing(false); }
  };

  const toggleCatVisibility = (catId) => {
    setVisibleCats(prev => {
      const allIds = customCategories.map(c => c.id);
      const current = prev || allIds;
      const next = current.includes(catId) ? current.filter(id => id !== catId) : [...current, catId];
      const result = next.length === 0 ? allIds : next;
      localStorage.setItem(LS_KEY, JSON.stringify(result));
      return result;
    });
  };

  const isCatVisible = (catId) => !visibleCats || visibleCats.includes(catId);

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: 'hsl(40, 18%, 97%)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 pt-16 sm:pt-8 pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {formatToday()}{totalUnread > 0 ? ` — ${totalUnread} nuove mail` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="ai-categorize-btn" onClick={handleAiCategorize} disabled={aiCategorizing} className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-xl border transition-all hover:shadow-sm" style={{ borderColor: '#6B6FD822', background: '#EDE8FF', color: '#6B6FD8' }}>
              <Sparkles size={14} className={aiCategorizing ? 'animate-pulse' : ''} />
              <span className="hidden sm:inline">{aiCategorizing ? 'AI...' : 'Categorizza AI'}</span>
              <span className="sm:hidden">{aiCategorizing ? 'AI...' : 'AI'}</span>
            </button>
            <button data-testid="compose-dashboard-btn" onClick={() => openCompose()} className="flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-xl border transition-all hover:shadow-sm" style={{ borderColor: '#E8EAED', background: 'white', color: '#333' }} onMouseEnter={e => e.currentTarget.style.background = '#F8F9FA'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>
              <PenSquare size={15} /> + Componi
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div data-search-container className="relative mb-8">
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all"
            style={{
              background: 'white',
              borderColor: searchFocused ? '#E2725B' : '#E8EAED',
              boxShadow: searchFocused ? '0 0 0 3px rgba(226,114,91,0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            {searching
              ? <Loader2 size={16} className="text-gray-400 animate-spin flex-shrink-0" />
              : <Search size={16} className="text-gray-400 flex-shrink-0" />
            }
            <input
              ref={searchRef}
              data-testid="dashboard-search-input"
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setSearchFocused(true)}
              placeholder="Cerca tra tutte le email..."
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: '#333', fontFamily: 'Manrope, sans-serif' }}
            />
            {hasActiveFilters && (
              <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: 'rgba(226,114,91,0.12)', color: '#E2725B' }}>
                Filtri attivi
              </span>
            )}
            {(searchQuery || hasActiveFilters) && (
              <button onClick={clearSearch} className="p-0.5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
                <X size={14} className="text-gray-400" />
              </button>
            )}
            <button
              data-testid="search-filters-toggle"
              onClick={() => setShowFilters(v => !v)}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all flex-shrink-0"
              style={{
                borderColor: showFilters ? '#E2725B' : '#E8EAED',
                background: showFilters ? 'rgba(226,114,91,0.08)' : 'transparent',
                color: showFilters ? '#E2725B' : '#888',
              }}>
              <SlidersHorizontal size={12} />
              Filtri
            </button>
          </div>

          {/* Advanced filters panel */}
          {showFilters && (
            <div className="mt-2 p-4 rounded-2xl border" style={{ background: 'white', borderColor: '#E8EAED' }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                    <Calendar size={11} /> Da data
                  </label>
                  <input data-testid="filter-date-from" type="date" value={filters.date_from}
                    onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                    className="w-full text-xs px-3 py-2 border rounded-lg focus:outline-none"
                    style={{ borderColor: '#E8EAED' }} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1">
                    <Calendar size={11} /> A data
                  </label>
                  <input data-testid="filter-date-to" type="date" value={filters.date_to}
                    onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                    className="w-full text-xs px-3 py-2 border rounded-lg focus:outline-none"
                    style={{ borderColor: '#E8EAED' }} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Cartella</label>
                  <select data-testid="filter-folder" value={filters.folder}
                    onChange={e => setFilters(f => ({ ...f, folder: e.target.value }))}
                    className="w-full text-xs px-3 py-2 border rounded-lg focus:outline-none appearance-none"
                    style={{ borderColor: '#E8EAED' }}>
                    <option value="">Tutte</option>
                    <option value="inbox">Inbox</option>
                    <option value="sent">Inviati</option>
                    <option value="drafts">Bozze</option>
                    <option value="spam">Spam</option>
                    <option value="trash">Cestino</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Account</label>
                  <select data-testid="filter-account" value={filters.account_id}
                    onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}
                    className="w-full text-xs px-3 py-2 border rounded-lg focus:outline-none appearance-none"
                    style={{ borderColor: '#E8EAED' }}>
                    <option value="">Tutti</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <div
                    data-testid="filter-has-attachments"
                    onClick={() => setFilters(f => ({ ...f, has_attachments: !f.has_attachments }))}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-all cursor-pointer"
                    style={{
                      borderColor: filters.has_attachments ? '#E2725B' : '#E8EAED',
                      background: filters.has_attachments ? 'rgba(226,114,91,0.08)' : 'white',
                      color: filters.has_attachments ? '#E2725B' : '#666',
                    }}>
                    <Paperclip size={12} />
                    Con allegati
                    {filters.has_attachments && <Check size={11} />}
                  </div>
                </label>
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <button onClick={resetFilters}
                      className="text-xs px-3 py-1.5 text-gray-400 hover:text-gray-700 transition-colors">
                      Reset filtri
                    </button>
                  )}
                  <button data-testid="apply-filters-btn" onClick={applyFilters}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white rounded-lg transition-all"
                    style={{ background: '#E2725B' }}>
                    <Search size={11} /> Cerca
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search results dropdown */}
          {searchFocused && searchQuery.length >= 2 && (
            <div
              className="absolute z-30 left-0 right-0 mt-2 rounded-2xl border overflow-hidden"
              style={{ background: 'white', borderColor: '#E8EAED', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
            >
              {searching && (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                  <Loader2 size={14} className="animate-spin" /> Ricerca in corso...
                </div>
              )}
              {!searching && searchResults.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  Nessun risultato per <strong>"{searchQuery}"</strong>
                </div>
              )}
              {!searching && searchResults.length > 0 && (
                <>
                  <div className="px-4 pt-3 pb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      {searchResults.length} risultat{searchResults.length === 1 ? 'o' : 'i'}
                    </span>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {searchResults.map(email => {
                      const dateStr = email.date_str
                        ? new Date(email.date_str).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
                        : '';
                      return (
                        <button
                          key={`${email.account_id}-${email.folder}-${email.uid}`}
                          data-testid={`search-result-${email.uid}`}
                          onClick={() => handleResultClick(email)}
                          className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-t"
                          style={{ borderColor: '#F5F0EA' }}
                        >
                          <div
                            className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                            style={{ background: email.account_color || '#E2725B', opacity: email.is_read ? 0.3 : 1 }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-sm truncate ${!email.is_read ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                                {email.from_name || email.from_addr}
                              </span>
                              <span className="text-xs text-gray-400 flex-shrink-0">{dateStr}</span>
                            </div>
                            <div className="text-xs text-gray-600 truncate">{email.subject}</div>
                            {email.preview && (
                              <div className="text-xs text-gray-400 truncate mt-0.5">{email.preview}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Inbox size={40} className="text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Nessun account configurato</h2>
            <p className="text-sm text-gray-400 mb-6">Aggiungi un account email per iniziare</p>
            <button data-testid="go-settings-btn" onClick={() => navigate('/settings')} className="px-6 py-2.5 text-sm font-medium text-white rounded-xl" style={{ background: '#E2725B' }}>Configura account</button>
          </div>
        ) : (
          <>
            {/* Tutte le mail */}
            <div data-testid="dashboard-all-mail-card" onClick={() => navigate('/inbox')} className="w-full flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl border cursor-pointer transition-all hover:shadow-sm mb-8" style={{ background: 'white', borderColor: '#E8EAED' }}>
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FFF0EC' }}>
                <Inbox size={20} style={{ color: '#E2725B' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-800 truncate">Mail in arrivo</div>
                <div className="text-xs sm:text-sm text-gray-500 truncate">Tutti gli account{totalUnread > 0 ? ` — ${totalUnread} nuove` : ''}</div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <span className="text-xl sm:text-2xl font-bold" style={{ color: '#E2725B' }}>
                  {Object.values(categoryCount).reduce((s, c) => s + (c.total || 0), 0) || '—'}
                </span>
                <ChevronRight size={18} className="text-gray-400" />
              </div>
              <button onClick={(e) => { e.stopPropagation(); handleRefresh(); }} disabled={refreshing} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0" title="Aggiorna">
                <RefreshCw size={14} className={`text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* CATEGORIE header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400">Categorie</h2>
              <button
                data-testid="customize-dashboard-btn"
                onClick={() => setShowCustomize(s => !s)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all"
                style={{ borderColor: showCustomize ? '#E2725B' : '#D9D2C8', background: showCustomize ? 'rgba(226,114,91,0.08)' : 'white', color: showCustomize ? '#E2725B' : '#7F8C8D' }}
              >
                <Settings2 size={12} />
                Personalizza
              </button>
            </div>

            {/* Customize panel */}
            {showCustomize && (
              <div className="mb-5 rounded-xl border p-4" style={{ borderColor: '#D9D2C8', background: 'white' }}>
                <p className="text-xs font-medium text-gray-500 mb-3">Seleziona le categorie da mostrare nella dashboard:</p>
                <div className="flex flex-wrap gap-2">
                  {customCategories.map(cat => {
                    const visible = isCatVisible(cat.id);
                    return (
                      <button
                        key={cat.id}
                        data-testid={`toggle-cat-visibility-${cat.id}`}
                        onClick={() => toggleCatVisibility(cat.id)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
                        style={{
                          borderColor: visible ? cat.color : '#D9D2C8',
                          background: visible ? cat.bg_color : 'white',
                          color: visible ? cat.color : '#7F8C8D',
                        }}
                      >
                        {visible && <Check size={10} />}
                        {cat.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-3">Le preferenze vengono salvate automaticamente.</p>
              </div>
            )}

            {/* Category cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleCategories.map((cat, i) => (
                <div key={cat.id} style={{ animationDelay: `${i * 0.05 + 0.05}s` }} className="animate-fade-in-up">
                  <CategoryCard
                    cat={cat}
                    counts={categoryCount[cat.id]}
                    lastEmail={lastEmails[cat.id]}
                    onClick={() => navigate(`/category/${cat.id}`)}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

